import type { AudienceContent, DgContent, Quote } from './artifacts.ts';
import { estimateGuideDurationSeconds } from './duration.ts';

/**
 * What a study's price is made of, and the local estimate of it.
 *
 * A price has two inputs: how many people, and how long each takes in whole buffered seconds.
 * Everything that decides whether an issued quote still prices a study goes through
 * `quoteInputsFor`, so "the inputs changed" is one comparison rather than a list of fields each
 * caller has to remember. Countries and filter selections are not inputs: they move Remesh's price
 * only through the screener polls they make, which the seconds already count.
 *
 * So the minutes a price is worked out on are not the Guide step's: `guideMinutes` is the guide
 * alone, and the price adds every screener poll. The participant reward Remesh commissions at
 * launch is a third figure, from Remesh's own estimator; the accepted price holds whatever it comes
 * to.
 *
 * The billable panel cost is Remesh's, read through the quote reader. What is here beside it is
 * `offlinePanelCost`, the same flex arithmetic for an environment with no Remesh, which can never
 * field what it prices, and `estimateQuote`, the non-billable estimate a study locked with no
 * purchase behind it is locked on.
 */

/** Remesh's public flex price: a dollar per participant-minute. */
export const FLEX_RATE_CENTS_PER_MINUTE = 100;

/**
 * How many pre-conversation polls the study will carry once its audience is applied.
 *
 * One per filter, because applying an audience creates a screener poll for every entry in the
 * payload — not one for the audience as a whole. One for the country, which every recruit audience
 * has. And one per screening question Sigora wrote, each a poll every participant answers.
 *
 * None of them exist while the study is being drafted and all of them exist by the time Remesh
 * prices it. Leaving any out — the injected country poll, a filter's, or a custom screener —
 * under-quotes the study, always in the same direction, and by the time the difference is
 * observable the audience has been applied and nothing about the study can be changed. Counting one
 * too many prices a few seconds a participant over, which is the direction to be wrong in.
 */
export const countScreenerPolls = (
  audience: Pick<AudienceContent, 'filters' | 'screeners'>,
): number => audience.filters.length + 1 + audience.screeners.length;

/** The two numbers a price is made of. */
export interface QuoteInputs {
  targetCompletes: number;
  /** Each participant's time, guide and screeners, buffered and rounded to the whole second. */
  durationSeconds: number;
}

/** The two numbers a price is made of, read off the artifacts they belong to. */
export const quoteInputsFor = ({
  guide,
  audience,
}: {
  guide: DgContent;
  audience: AudienceContent;
}): QuoteInputs => ({
  targetCompletes: audience.target_completes,
  // The audience is what decides how many screeners there are, which is why the duration is not a
  // property of the guide alone: adding a demographic or a screener lengthens the study.
  durationSeconds: estimateGuideDurationSeconds(guide, countScreenerPolls(audience)),
});

/** Whether two sets of inputs price the same study. */
export const sameQuoteInputs = (a: QuoteInputs, b: QuoteInputs): boolean =>
  a.targetCompletes === b.targetCompletes && a.durationSeconds === b.durationSeconds;

/** What a panel of `targetCompletes` people for `durationSeconds` each costs, in integer cents. */
export interface PanelCost {
  targetCompletes: number;
  durationSeconds: number;
  panelCostCents: number;
  currency: 'USD';
}

/**
 * The flex price worked out here rather than read from Remesh, in integer cents.
 *
 * The rounding order is Remesh's: the per-complete price is rounded to the cent first and then
 * multiplied, so rounding the product instead disagrees by up to half a cent per participant.
 * Rounding here is half up where Python's is half to even, so a per-complete price landing on half
 * a cent can differ by one; nothing billable is ever this number.
 */
export const offlinePanelCost = (inputs: QuoteInputs): PanelCost => ({
  targetCompletes: inputs.targetCompletes,
  durationSeconds: inputs.durationSeconds,
  panelCostCents:
    Math.round((FLEX_RATE_CENTS_PER_MINUTE * inputs.durationSeconds) / 60) * inputs.targetCompletes,
  currency: 'USD',
});

/**
 * The non-billable estimate of a study's price, for a lock with no purchase behind it: the dev
 * freeze and a runtime check's run. It is no issued quote — it has no id and no window — and
 * nothing is charged on it.
 */
export const estimateQuote = ({
  inputs,
  now = () => new Date(),
}: {
  inputs: QuoteInputs;
  now?: () => Date;
}): Quote => ({
  total_cents: offlinePanelCost(inputs).panelCostCents,
  currency: 'USD',
  target_completes: inputs.targetCompletes,
  estimated_duration_minutes: inputs.durationSeconds / 60,
  computed_at: now().toISOString(),
});

/**
 * Why a price was refused for good (`quote_refused`, in `detail.reason`): no retry changes any of
 * them. One list, which the server refuses with and Launch says a sentence for.
 */
export const QUOTE_REFUSAL_REASONS = [
  'target_completes_out_of_range',
  'not_entitled',
  'remesh_refused',
] as const;
export type QuoteRefusalReason = (typeof QUOTE_REFUSAL_REASONS)[number];

/**
 * The sentence a researcher is shown for each permanent refusal, and what it asks of them: the
 * refusal's `message`, and what Launch says. Written once, here.
 */
export const QUOTE_REFUSAL_MESSAGES: Record<QuoteRefusalReason, string> = {
  target_completes_out_of_range:
    'This number of people cannot be recruited. Change the number in the Audience step.',
  not_entitled: 'This workspace cannot buy recruitment, so no price can be worked out for it.',
  remesh_refused: 'The price for this study was refused. Asking again will not change that.',
};

/**
 * Whether an issued quote is still guaranteed: strictly before `expires_at`, so at `expires_at` it
 * has lapsed. Decided on the caller's clock rather than the database's, so every edge is testable.
 */
export const isQuoteLive = (quote: { expires_at: string }, now: Date): boolean =>
  now.getTime() < Date.parse(quote.expires_at);
